/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { type CrossAppItemResponse } from '@atlassian/navigation-bff-data-provider/universal-create';
import { ATI_PRESETS, type UniversalCreateSourceProduct } from '@atlassian/universal-create-common';
import { withLoomVideoConfig } from '@atlassian/universal-create-integrations/loom';

import { GenericErrorBoundary } from '../../../services';
import type { CreationContext } from '../../../types/crossAppCreation';
import { getCreateContentLoader } from '../../integrations/forms/formLoader';

import { CrossAppItemBase, type CrossAppItemBaseProps } from './CrossAppItemBase';
import { CrossAppLinkItem } from './primitives/CrossAppLinkItem';
import { GicIssueCreateItem } from './primitives/GicIssueCreateItem';
import { LoomVideoItem as CrossAppLoomVideoItem } from './primitives/LoomVideoItem';


export interface CrossAppItemProps {
	/**
	 * Additional context for the component
	 */
	context: CreationContext;

	/**
	 * Optional analytics injection callbacks for click tracking
	 */
	injectClickAnalytics?: {
		onClick?: (isRightClick: boolean) => void;
	};

	/**
	 * The source product context
	 */
	product: UniversalCreateSourceProduct;

	/**
	 * The response item from Universal Create
	 */
	responseItem: CrossAppItemResponse;

	/**
	 * The sub-product context
	 */
	subProduct?: string;
}

interface LoomVideoConfigProps extends CrossAppItemBaseProps {
	product: CrossAppItemProps['product'];
	subProduct?: CrossAppItemProps['subProduct'];
}

function renderPlatformCrossAppItem({
	responseItem,
	context,
	product,
	subProduct,
	injectClickAnalytics,
}: CrossAppItemProps) {
	switch (responseItem.ati) {
		case ATI_PRESETS.WORK_ITEM: {
			if (!fg('navx_enable_gic')) {
				return null; // should not show this item if the feature flag is off
			}

			return (
				<GicIssueCreateItem
					responseItem={responseItem}
					context={context}
					injectClickAnalytics={injectClickAnalytics}
				/>
			);
		}
		case ATI_PRESETS.LOOM: {
			const LoomVideoItem = fg('navx-2470-migrate-loom-integration')
				? withLoomVideoConfig<LoomVideoConfigProps>(CrossAppItemBase)
				: CrossAppLoomVideoItem;

			return (
				<LoomVideoItem
					responseItem={responseItem}
					context={context}
					product={product}
					injectClickAnalytics={injectClickAnalytics}
					subProduct={subProduct}
				/>
			);
		}
		case ATI_PRESETS.GOAL:
		case ATI_PRESETS.PROJECT:
		case ATI_PRESETS.TEAM: {
			if (!fg('navx_uc_platformisation_forms_enabled')) {
				return null; // should not show this item if the feature flag is off
			}

			const loadCreateContent = getCreateContentLoader(responseItem.ati);

			const customisedItemConfig = {
				loadCreateContent,
				onClickCloseMenu: !loadCreateContent,
			};

			return (
				<CrossAppItemBase
					responseItem={responseItem}
					context={context}
					customisedItemConfig={customisedItemConfig}
					injectClickAnalytics={injectClickAnalytics}
				/>
			);
		}
		default:
			return undefined;
	}
}

function renderCrossAppItem({
	responseItem,
	context,
	product,
	subProduct,
	injectClickAnalytics,
}: CrossAppItemProps) {
	switch (responseItem.type) {
		case 'platform':
			return renderPlatformCrossAppItem({
				responseItem,
				context,
				product,
				subProduct,
				injectClickAnalytics,
			});
		case 'link':
			return (
				<CrossAppLinkItem
					responseItem={responseItem}
					context={context}
					product={product}
					injectClickAnalytics={injectClickAnalytics}
				/>
			);
		default:
			break;
	}

	return undefined;
}

function CrossAppItemInner({
	responseItem,
	context,
	product,
	subProduct,
	injectClickAnalytics,
}: CrossAppItemProps) {
	const renderedItem = renderCrossAppItem({
		responseItem,
		context,
		product,
		subProduct,
		injectClickAnalytics,
	});

	if (renderedItem !== undefined) {
		return renderedItem;
	}

	// Return general cross-app item with no customisedItemConfig
	return (
		<CrossAppItemBase
			responseItem={responseItem}
			context={context}
			injectClickAnalytics={injectClickAnalytics}
		/>
	);
}

/**
 * A component that renders a cross-app creation item.
 * Wrapped by the ErrorBoundary component to handle errors so that it won't break the whole menu.
 *
 * @param props - @see CrossAppItemProps
 * @returns - JSX.Element @see CrossAppItemInner
 */
export function CrossAppItem(props: CrossAppItemProps) {
	return (
		<GenericErrorBoundary name="cross-app-item">
			<CrossAppItemInner {...props} />
		</GenericErrorBoundary>
	);
}
